// ============================================================
//  TWO-PERSON FIREBASE CHAT — TWO STATIC USERS
//  Pick your identity (Person A or Person B) and chat.
//  No waiting. Messages stored in Firebase Firestore.
// ============================================================

import { useState, useEffect, useRef, useCallback } from "react";
import { initializeApp } from "firebase/app";
import {
  getFirestore,
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  doc,
  setDoc,
} from "firebase/firestore";

// ─── YOUR FIREBASE CONFIG ────────────────────────────────────
const firebaseConfig = {
  apiKey: "AIzaSyAAIfV7ME_2DJGSaE9DC3exXrtRk_5hDLo",
  authDomain: "real-talk-78c16.firebaseapp.com",
  projectId: "real-talk-78c16",
  storageBucket: "real-talk-78c16.firebasestorage.app",
  messagingSenderId: "421588567346",
  appId: "1:421588567346:web:2bcaf391397699a8b0832b",
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

const CHAT_ID = "private-room-001";

// ── TWO FIXED USERS — change names/colors here ──────────────
const USERS = {
  A: { id: "user_A", name: "S",  avatar: "S", color: "#1a1814", textColor: "#f5f0e8" },
  B: { id: "user_B", name: "G", avatar: "G", color: "#4a7c59", textColor: "#f5f0e8" },
};

// ─── HELPERS ─────────────────────────────────────────────────
function formatTime(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatDay(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString([], { month: "long", day: "numeric" });
}

// ─── STYLES ──────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@300;400;500;600&family=Instrument+Serif:ital@0;1&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Syne', sans-serif; background: #f5f0e8; color: #1a1814; min-height: 100vh; }
  .app { display: flex; flex-direction: column; height: 100vh; background: #f5f0e8; }

  /* ── PICK SCREEN ── */
  .pick-screen {
    display: flex; flex-direction: column; align-items: center;
    justify-content: center; height: 100vh; padding: 2rem; gap: 0;
  }
  .pick-mark {
    width: 54px; height: 54px; border-radius: 50%; background: #1a1814;
    display: flex; align-items: center; justify-content: center; margin-bottom: 1.25rem;
  }
  .pick-title {
    font-family: 'Instrument Serif', serif; font-size: 2.4rem;
    font-weight: 400; color: #1a1814; margin-bottom: 0.3rem;
  }
  .pick-sub {
    font-size: 0.75rem; color: #8a8070; letter-spacing: 0.12em;
    text-transform: uppercase; margin-bottom: 2.5rem;
  }
  .pick-label {
    font-size: 0.72rem; color: #8a8070; letter-spacing: 0.1em;
    text-transform: uppercase; margin-bottom: 1rem;
  }
  .pick-cards { display: flex; gap: 1rem; width: 100%; max-width: 420px; }
  .pick-card {
    flex: 1; background: #fff; border: 1.5px solid #e8e0d0; border-radius: 20px;
    padding: 1.75rem 1.25rem; display: flex; flex-direction: column;
    align-items: center; gap: 0.75rem; cursor: pointer;
    transition: border-color 0.2s, transform 0.15s, box-shadow 0.2s;
  }
  .pick-card:hover {
    border-color: #1a1814; transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  }
  .pick-avatar {
    width: 52px; height: 52px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 1.2rem; font-weight: 600; letter-spacing: 0;
  }
  .pick-name { font-size: 1rem; font-weight: 500; color: #1a1814; }
  .pick-hint { font-size: 0.72rem; color: #b0a898; }

  /* ── HEADER ── */
  .chat-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 0.85rem 1.25rem; background: #fff;
    border-bottom: 1px solid #e8e0d0; flex-shrink: 0;
  }
  .header-left { display: flex; align-items: center; gap: 0.75rem; }
  .header-title { font-family: 'Instrument Serif', serif; font-size: 1.1rem; color: #1a1814; }
  .header-partner {
    display: flex; align-items: center; gap: 0.5rem;
    background: #f5f0e8; border: 1px solid #e8e0d0;
    border-radius: 20px; padding: 0.25rem 0.7rem 0.25rem 0.4rem;
  }
  .mini-avatar {
    width: 20px; height: 20px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 0.6rem; font-weight: 600;
  }
  .partner-name-text { font-size: 0.78rem; color: #5a5248; }
  .header-right { display: flex; align-items: center; gap: 0.5rem; }
  .header-me {
    font-size: 0.78rem; color: #8a8070; background: #f5f0e8;
    border: 1px solid #e8e0d0; border-radius: 20px; padding: 0.25rem 0.7rem;
  }
  .switch-btn {
    background: transparent; border: 1px solid #e8e0d0; border-radius: 8px;
    padding: 0.3rem 0.75rem; font-family: 'Syne', sans-serif;
    font-size: 0.75rem; color: #8a8070; cursor: pointer; transition: all 0.2s;
  }
  .switch-btn:hover { border-color: #1a1814; color: #1a1814; }

  /* ── MESSAGES ── */
  .messages-area {
    flex: 1; overflow-y: auto; padding: 1.25rem;
    display: flex; flex-direction: column; gap: 0.4rem;
  }
  .messages-area::-webkit-scrollbar { width: 4px; }
  .messages-area::-webkit-scrollbar-track { background: transparent; }
  .messages-area::-webkit-scrollbar-thumb { background: #e8e0d0; border-radius: 2px; }

  .day-divider { display: flex; align-items: center; gap: 0.75rem; margin: 0.75rem 0; }
  .day-divider::before, .day-divider::after { content: ''; flex: 1; height: 1px; background: #e8e0d0; }
  .day-label { font-size: 0.68rem; color: #b0a898; letter-spacing: 0.08em; text-transform: uppercase; white-space: nowrap; }

  .msg-row { display: flex; flex-direction: column; }
  .msg-row.mine { align-items: flex-end; }
  .msg-row.theirs { align-items: flex-start; }

  .msg-sender { font-size: 0.68rem; color: #b0a898; margin-bottom: 0.2rem; padding: 0 0.25rem; }

  .msg-bubble {
    max-width: 68%; padding: 0.6rem 0.95rem;
    border-radius: 18px; font-size: 0.92rem;
    line-height: 1.55; word-break: break-word;
  }
  .msg-row.mine .msg-bubble  { border-bottom-right-radius: 5px; }
  .msg-row.theirs .msg-bubble { border-bottom-left-radius: 5px; border: 1px solid #e8e0d0; }

  .msg-time { font-size: 0.65rem; color: #c0b8a8; margin-top: 0.2rem; padding: 0 0.25rem; }

  .system-msg {
    text-align: center; font-size: 0.75rem; color: #b0a898;
    font-style: italic; margin: 0.5rem 0;
    font-family: 'Instrument Serif', serif;
  }
  .empty-state { flex: 1; display: flex; align-items: center; justify-content: center; }
  .empty-state p {
    font-family: 'Instrument Serif', serif; font-style: italic;
    font-size: 1rem; color: #c0b8a8;
  }

  /* TYPING */
  .typing-indicator {
    display: flex; align-items: center; gap: 4px;
    padding: 0.5rem 0.9rem; background: #fff;
    border: 1px solid #e8e0d0; border-radius: 18px;
    border-bottom-left-radius: 5px; width: fit-content; margin: 0.2rem 0;
  }
  .typing-dot {
    width: 5px; height: 5px; border-radius: 50%;
    background: #c0b8a8; animation: tdot 1.2s ease-in-out infinite;
  }
  .typing-dot:nth-child(2) { animation-delay: 0.2s; }
  .typing-dot:nth-child(3) { animation-delay: 0.4s; }
  @keyframes tdot { 0%,60%,100% { transform: translateY(0); } 30% { transform: translateY(-4px); } }

  /* INPUT */
  .input-bar {
    display: flex; align-items: flex-end; gap: 0.75rem;
    padding: 1rem 1.25rem; background: #fff;
    border-top: 1px solid #e8e0d0; flex-shrink: 0;
  }
  .msg-input {
    flex: 1; background: #f5f0e8; border: 1px solid #e8e0d0;
    border-radius: 14px; padding: 0.65rem 1rem; color: #1a1814;
    font-family: 'Syne', sans-serif; font-size: 0.9rem; outline: none;
    resize: none; max-height: 130px; line-height: 1.5; transition: border-color 0.2s;
  }
  .msg-input:focus { border-color: #1a1814; }
  .msg-input::placeholder { color: #c0b8a8; }
  .btn-send {
    width: 42px; height: 42px; border-radius: 50%; border: none;
    cursor: pointer; display: flex; align-items: center; justify-content: center;
    flex-shrink: 0; transition: opacity 0.2s, transform 0.15s;
  }
  .btn-send:hover { opacity: 0.75; }
  .btn-send:active { transform: scale(0.93); }
  .btn-send:disabled { opacity: 0.25; cursor: not-allowed; }
`;

// ─── PICK SCREEN ──────────────────────────────────────────────
function PickScreen({ onPick }) {
  return (
    <div className="pick-screen">
      <div className="pick-mark">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#f5f0e8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      </div>
      <div className="pick-title">Real Talk</div>
      <div className="pick-sub">Private · Two Person Room</div>
      <div className="pick-label">Who are you?</div>
      <div className="pick-cards">
        {Object.entries(USERS).map(([key, user]) => (
          <div className="pick-card" key={key} onClick={() => onPick(user)}>
            <div
              className="pick-avatar"
              style={{ background: user.color, color: user.textColor }}
            >
              {user.avatar}
            </div>
            <div className="pick-name">{user.name}</div>
            <div className="pick-hint">Tap to enter</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── CHAT VIEW ────────────────────────────────────────────────
function ChatView({ me, onSwitch }) {
  const partner = Object.values(USERS).find(u => u.id !== me.id);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [partnerTyping, setPartnerTyping] = useState(false);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const typingTimer = useRef(null);

  // Listen to messages
  useEffect(() => {
    const q = query(
      collection(db, "rooms", CHAT_ID, "messages"),
      orderBy("createdAt", "asc")
    );
    const unsub = onSnapshot(q, snap => {
      setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 60);
    });
    return unsub;
  }, []);

  // Listen to typing indicator
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "rooms", CHAT_ID), snap => {
      if (!snap.exists()) return;
      const typing = snap.data()?.typing || {};
      setPartnerTyping(!!typing[partner.id]);
    });
    return unsub;
  }, [partner.id]);

  async function setTyping(val) {
    clearTimeout(typingTimer.current);
    await setDoc(
      doc(db, "rooms", CHAT_ID),
      { typing: { [me.id]: val } },
      { merge: true }
    );
    if (val) {
      typingTimer.current = setTimeout(() => setTyping(false), 2500);
    }
  }

  async function sendMessage() {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setText("");
    setSending(true);
    clearTimeout(typingTimer.current);
    await setTyping(false);
    try {
      await addDoc(collection(db, "rooms", CHAT_ID, "messages"), {
        type: "chat",
        text: trimmed,
        senderId: me.id,
        senderName: me.name,
        createdAt: serverTimestamp(),
      });
    } catch (e) { console.error(e); }
    setSending(false);
  }

  // Build render list with day dividers
  const rendered = [];
  let lastDay = null;
  for (const msg of messages) {
    if (msg.createdAt) {
      const day = formatDay(msg.createdAt);
      if (day !== lastDay) {
        rendered.push({ type: "divider", day, id: "div_" + msg.id });
        lastDay = day;
      }
    }
    rendered.push(msg);
  }

  const chatCount = messages.filter(m => m.type === "chat").length;

  return (
    <>
      <div className="chat-header">
        <div className="header-left">
          <span className="header-title">Real Talk</span>
          <div className="header-partner">
            <div
              className="mini-avatar"
              style={{ background: partner.color, color: partner.textColor }}
            >
              {partner.avatar}
            </div>
            <span className="partner-name-text">{partner.name}</span>
          </div>
        </div>
        <div className="header-right">
          <span className="header-me">You: {me.name}</span>
          <button className="switch-btn" onClick={onSwitch}>Switch user</button>
        </div>
      </div>

      <div className="messages-area">
        {chatCount === 0 && (
          <div className="empty-state"><p>The room is quiet… say something.</p></div>
        )}
        {rendered.map(item => {
          if (item.type === "divider") {
            return (
              <div className="day-divider" key={item.id}>
                <span className="day-label">{item.day}</span>
              </div>
            );
          }
          if (item.type === "system") {
            return <div className="system-msg" key={item.id}>— {item.text} —</div>;
          }

          const mine = item.senderId === me.id;
          const sender = Object.values(USERS).find(u => u.id === item.senderId);

          return (
            <div className={`msg-row ${mine ? "mine" : "theirs"}`} key={item.id}>
              {!mine && <div className="msg-sender">{item.senderName}</div>}
              <div
                className="msg-bubble"
                style={mine
                  ? { background: me.color, color: me.textColor }
                  : { background: "#fff", color: "#1a1814" }
                }
              >
                {item.text}
              </div>
              <div className="msg-time">{formatTime(item.createdAt)}</div>
            </div>
          );
        })}

        {partnerTyping && (
          <div className="msg-row theirs">
            <div className="msg-sender">{partner.name}</div>
            <div className="typing-indicator">
              <div className="typing-dot" />
              <div className="typing-dot" />
              <div className="typing-dot" />
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="input-bar">
        <textarea
          className="msg-input"
          rows={1}
          placeholder={`Message as ${me.name}…`}
          value={text}
          onChange={e => {
            setText(e.target.value);
            setTyping(e.target.value.length > 0);
            e.target.style.height = "auto";
            e.target.style.height = Math.min(e.target.scrollHeight, 130) + "px";
          }}
          onKeyDown={e => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
          }}
        />
        <button
          className="btn-send"
          style={{ background: me.color }}
          onClick={sendMessage}
          disabled={!text.trim() || sending}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f5f0e8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </div>
    </>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────
export default function App() {
  const [me, setMe] = useState(null);

  return (
    <div className="app">
      <style>{styles}</style>
      {!me
        ? <PickScreen onPick={setMe} />
        : <ChatView me={me} onSwitch={() => setMe(null)} />
      }
    </div>
  );
}